/**
 * Queries for `public.ayah_reads` — explicit "I've read this ayah" marks.
 *
 * Distinct from `reading_progress` (latest position) and `bookmarks`
 * (save for later). One row per (user, surah, ayah). Insert/delete only.
 *
 * RLS is enforced server-side; the queries here don't need to filter by
 * user_id because the policies do it for us.
 */

import { supabase } from '@/lib/supabase/client';

export type AyahRead = {
  user_id: string;
  surah_id: number;
  ayah_number: number;
  read_at: string;
};

/**
 * Returns every ayah the current user has marked read in `surahId`.
 * Used by the Reader to render the progress card and per-ayah pills.
 */
export async function getReadsForSurah(surahId: number): Promise<AyahRead[]> {
  const { data, error } = await supabase
    .from('ayah_reads')
    .select('*')
    .eq('surah_id', surahId);
  if (error) throw error;
  return (data as AyahRead[] | null) ?? [];
}

/**
 * Idempotent — already-read rows hit the PK and are ignored.
 */
export async function markAyahRead(input: {
  user_id: string;
  surah_id: number;
  ayah_number: number;
}): Promise<void> {
  const { error } = await supabase
    .from('ayah_reads')
    .upsert(
      {
        user_id: input.user_id,
        surah_id: input.surah_id,
        ayah_number: input.ayah_number,
      },
      { onConflict: 'user_id,surah_id,ayah_number', ignoreDuplicates: true },
    );
  if (error) throw error;
}

/**
 * Marks an inclusive ayah range as read in one round-trip.
 * Existing rows are ignored by the composite PK conflict target.
 */
export async function markAyahRangeRead(input: {
  user_id: string;
  surah_id: number;
  start_ayah: number;
  end_ayah: number;
}): Promise<void> {
  const rows = Array.from(
    { length: input.end_ayah - input.start_ayah + 1 },
    (_, i) => ({
      user_id: input.user_id,
      surah_id: input.surah_id,
      ayah_number: input.start_ayah + i,
    }),
  );

  const { error } = await supabase
    .from('ayah_reads')
    .upsert(rows, {
      onConflict: 'user_id,surah_id,ayah_number',
      ignoreDuplicates: true,
    });
  if (error) throw error;
}

/**
 * Marks every ayah as read across an inclusive surah range in one write.
 * The caller provides the ayah counts so this layer stays data-source agnostic.
 */
export async function markSurahRangeRead(input: {
  user_id: string;
  start_surah_id: number;
  end_surah_id: number;
  surahAyahCounts: Array<{ surahId: number; ayahCount: number }>;
}): Promise<void> {
  const rows = input.surahAyahCounts.flatMap(({ surahId, ayahCount }) =>
    Array.from({ length: ayahCount }, (_, i) => ({
      user_id: input.user_id,
      surah_id: surahId,
      ayah_number: i + 1,
    })),
  );

  if (rows.length === 0) return;

  const { error } = await supabase
    .from('ayah_reads')
    .upsert(rows, {
      onConflict: 'user_id,surah_id,ayah_number',
      ignoreDuplicates: true,
    });
  if (error) throw error;
}

/**
 * Idempotent — deleting a row that doesn't exist is a no-op.
 */
export async function unmarkAyahRead(input: {
  user_id: string;
  surah_id: number;
  ayah_number: number;
}): Promise<void> {
  const { error } = await supabase
    .from('ayah_reads')
    .delete()
    .eq('user_id', input.user_id)
    .eq('surah_id', input.surah_id)
    .eq('ayah_number', input.ayah_number);
  if (error) throw error;
}

/**
 * Reset every read mark for a single surah. Destructive — once the
 * rows are gone, that surah's trophy is lost until the user completes
 * it again. Callers MUST gate this behind a confirmation dialog.
 *
 * MVP behaviour is hard delete. If we later want "all-time ayahs read"
 * stats we'll switch to a soft-reset model via a surah_resets table,
 * but the public API of this function would stay the same.
 */
export async function resetSurahProgress(input: {
  user_id: string;
  surah_id: number;
}): Promise<void> {
  const { error } = await supabase
    .from('ayah_reads')
    .delete()
    .eq('user_id', input.user_id)
    .eq('surah_id', input.surah_id);
  if (error) throw error;
}
